import { LEARN_MOVED } from '@feral/shared/client'
import { createRootRoute, createRoute, createRouter, Navigate, redirect } from '@tanstack/react-router'
import type React from 'react'
import { lazy, Suspense } from 'react'
import { OLD_FAVORITES, yoursSearch } from '../features/groups/tabs.js'
import { partnerSearch } from '../features/partners/common.js'
import { wallSearch } from '../features/wall/tabs.js'
import { ErrorScreen } from './ErrorScreen.js'
import { FrameGuard } from './FrameGuard.js'
import { NotFound } from './NotFound.js'
import { RouteTitle } from './RouteTitle.js'
import { Shell } from './Shell.js'
import { recordTrail } from './trail.js'

// An unknown path is never a dead end: it falls back to the map, which sends a signed-out visitor home.
// The service worker is registered at the root, so every screen has it, the onboarding primers
// included (iOS testing: push could not subscribe before the map had ever opened). Its code loads just
// after the first screen, not with it (NFR-PERF-07).
const PwaBridge = lazy(() => import('./PwaBridge.js').then((m) => ({ default: m.PwaBridge })))
const root = createRootRoute({
  component: () => (
    <>
      <Suspense fallback={null}>
        <PwaBridge />
      </Suspense>
      <RouteTitle />
      <FrameGuard />
    </>
  ),
  notFoundComponent: NotFound,
})
const str = (v: unknown) => (typeof v === 'string' ? v : undefined)
const fallback = null
const wrap = (C: React.LazyExoticComponent<() => React.ReactElement | null>) => () => (
  <Suspense fallback={fallback}>
    <C />
  </Suspense>
)
const L = <T extends Record<string, () => React.ReactElement | null>>(
  load: () => Promise<T>,
  name: keyof T & string,
) => wrap(lazy(() => load().then((m) => ({ default: m[name] as () => React.ReactElement | null }))))

// ── entry: full screens, no shell ───────────────────────────────────────
// Lazy too: a returning member opens straight onto the map and never needs these (NFR-PERF-07).
const AgeGate = L(() => import('../features/entry/AgeGate.js'), 'AgeGate')
const AnonSignup = L(() => import('../features/entry/AnonSignup.js'), 'AnonSignup')
const InviteLanding = L(() => import('../features/entry/Invite.js'), 'InviteLanding')
const PendingSignup = L(() => import('../features/entry/Invite.js'), 'PendingSignup')
const FrontDoor = L(() => import('../features/entry/FrontDoor.js'), 'FrontDoor')
const Landing = L(() => import('../features/entry/Landing.js'), 'Landing')
const Install = L(() => import('../features/entry/Onboarding.js'), 'Install')
const LocationPrimer = L(() => import('../features/entry/Onboarding.js'), 'LocationPrimer')
const NotifPrimer = L(() => import('../features/entry/Onboarding.js'), 'NotifPrimer')
const Rules = L(() => import('../features/entry/Onboarding.js'), 'Rules')
const SignIn = L(() => import('../features/entry/SignIn.js'), 'SignIn')
const SignInVerify = L(() => import('../features/entry/SignIn.js'), 'SignInVerify')
const ContinueFromSafari = L(() => import('../features/entry/SignIn.js'), 'ContinueFromSafari')
const PasskeyOffer = L(() => import('../features/entry/SignIn.js'), 'PasskeyOffer')
/** `/` only decides where you land (decision 135); the front door itself is `/enter` and never sends you away. */
const frontDoorRoute = createRoute({ getParentRoute: () => root, path: '/', component: FrontDoor })
const landing = createRoute({ getParentRoute: () => root, path: '/enter', component: Landing })
const age = createRoute({
  getParentRoute: () => root,
  path: '/age',
  component: AgeGate,
  validateSearch: (s: Record<string, unknown>): { next?: string; av?: string } => ({
    next: str(s.next),
    av: str(s.av),
  }),
})
const anon = createRoute({ getParentRoute: () => root, path: '/anon', component: AnonSignup })
const invite = createRoute({ getParentRoute: () => root, path: '/i/$code', component: InviteLanding })
const join = createRoute({
  getParentRoute: () => root,
  path: '/join',
  component: PendingSignup,
  /** `e`: an emailed link's proof of the address, carried through so sign-up needs no code */
  validateSearch: (s: Record<string, unknown>): { code?: string; e?: string } => ({
    code: str(s.code),
    e: str(s.e),
  }),
})
const rules = createRoute({
  getParentRoute: () => root,
  path: '/rules',
  component: Rules,
  validateSearch: (s: Record<string, unknown>): { after?: string } => ({ after: str(s.after) }),
})
const terms = createRoute({
  getParentRoute: () => root,
  path: '/terms',
  component: L(() => import('../features/legal/LegalPages.js'), 'Terms'),
})
const privacy = createRoute({
  getParentRoute: () => root,
  path: '/privacy',
  component: L(() => import('../features/legal/LegalPages.js'), 'Privacy'),
})
const install = createRoute({ getParentRoute: () => root, path: '/install', component: Install })
/** Sign-up's first step (bugs.md); `next` is where sign-up goes after the age gate, as on /age. */
const location = createRoute({
  getParentRoute: () => root,
  path: '/location',
  component: LocationPrimer,
  validateSearch: (s: Record<string, unknown>): { next?: string } => ({ next: str(s.next) }),
})
const notify = createRoute({ getParentRoute: () => root, path: '/notify', component: NotifPrimer })
/** `from`, and `src` carried on: the front door sent them here (decision 135), for the funnel. */
const signIn = createRoute({
  getParentRoute: () => root,
  path: '/sign-in',
  component: SignIn,
  validateSearch: (s: Record<string, unknown>): { from?: string; src?: string } => ({
    from: str(s.from),
    src: str(s.src),
  }),
})
const signInPasskey = createRoute({
  getParentRoute: () => root,
  path: '/sign-in/passkey',
  component: PasskeyOffer,
})
const continueFromSafari = createRoute({
  getParentRoute: () => root,
  path: '/continue',
  component: ContinueFromSafari,
})
const leave = createRoute({
  getParentRoute: () => root,
  path: '/leave',
  component: L(() => import('../features/safety/Leave.js'), 'Leave'),
})
const signInVerify = createRoute({
  getParentRoute: () => root,
  path: '/sign-in/verify',
  component: SignInVerify,
  validateSearch: (s: Record<string, unknown>): { token?: string } => ({ token: str(s.token) }),
})

// ── the shell: the default view behind, everything else a popup (FR-MAP-15) ─
const shell = createRoute({ getParentRoute: () => root, id: '_shell', component: Shell })
const map = createRoute({
  getParentRoute: () => shell,
  path: '/map',
  component: () => null,
  /** `at=lat,lng` flies the map to a point, used by a shared location in chat */
  validateSearch: (s: Record<string, unknown>): { at?: string } => ({ at: str(s.at) }),
})
/** The grid (bugs.md): the people and spots in reach as tiles, over the same map (MapScreen draws it). */
const grid = createRoute({ getParentRoute: () => shell, path: '/grid', component: () => null })
const wall = createRoute({
  getParentRoute: () => shell,
  path: '/wall',
  component: L(() => import('../features/wall/Wall.js'), 'Wall'),
  // Old tabs and halves land where they live now (wall/tabs.ts).
  validateSearch: wallSearch,
})
const wallSaved = createRoute({
  getParentRoute: () => shell,
  path: '/wall/saved',
  component: L(() => import('../features/wall/Wall.js'), 'WallSaved'),
})
const gate = createRoute({
  getParentRoute: () => shell,
  path: '/gate',
  component: L(() => import('../features/trust/Gate.js'), 'Gate'),
  validateSearch: (
    s: Record<string, unknown>,
  ): { tab?: 'eligible' | 'anonymous' | 'vetted' | 'chain' | 'places'; message?: string } => ({
    // A reminder about someone you vouched for opens their chat from Your people (decision 179).
    ...(typeof s.message === 'string' && /^[0-9a-f-]{36}$/i.test(s.message) ? { message: s.message } : {}),
    tab:
      s.tab === 'eligible' ||
      s.tab === 'anonymous' ||
      s.tab === 'vetted' ||
      s.tab === 'chain' ||
      s.tab === 'places'
        ? s.tab
        : undefined,
  }),
})
const gateRecord = createRoute({
  getParentRoute: () => shell,
  path: '/gate/record',
  component: L(() => import('../features/trust/Capture.js'), 'Capture'),
})
const gateReview = createRoute({
  getParentRoute: () => shell,
  path: '/gate/review/$id',
  component: L(() => import('../features/trust/Review.js'), 'Review'),
})
const inbox = createRoute({
  getParentRoute: () => shell,
  path: '/inbox',
  component: L(() => import('../features/chat/Inbox.js'), 'Inbox'),
  validateSearch: (
    s: Record<string, unknown>,
  ): { tab?: 'inbox' | 'taps' | 'requests' | 'activity'; item?: string } => ({
    // Knocks joined Requests (bugs.md): an old `?tab=knocks` link lands there.
    tab:
      s.tab === 'knocks'
        ? 'requests'
        : s.tab === 'taps' || s.tab === 'requests' || s.tab === 'activity'
          ? s.tab
          : undefined,
    /** the Activity row a flashed notification opens to (app/Flashes.tsx) */
    item: str(s.item),
  }),
})
// A thread opens inside the Inbox, under its tabs (bugs.md): the Inbox reads the id from the path, so
// the docked copy (app/pin.tsx) shows it too, and the Inbox stays mounted while threads come and go.
const thread = createRoute({ getParentRoute: () => inbox, path: '$id', component: () => null })
// The user icon's screen (WF-SET-01): everything about you and this phone, Settings folded in.
const you = createRoute({
  getParentRoute: () => shell,
  path: '/you',
  component: L(() => import('../features/settings/You.js'), 'You'),
})
// Settings became the You screen; old links still land there.
const settings = createRoute({
  getParentRoute: () => shell,
  path: '/settings',
  component: () => <Navigate to="/you" replace />,
})
const notifications = createRoute({
  getParentRoute: () => shell,
  path: '/settings/notifications',
  component: L(() => import('../features/settings/Notifications.js'), 'Notifications'),
})
const quickReplies = createRoute({
  getParentRoute: () => shell,
  path: '/settings/quick-replies',
  component: L(() => import('../features/settings/QuickReplies.js'), 'QuickReplies'),
})
const plus = createRoute({
  getParentRoute: () => shell,
  path: '/plus',
  component: L(() => import('../features/plus/Plus.js'), 'Plus'),
})
const plusSite = createRoute({
  getParentRoute: () => shell,
  path: '/plus/site',
  component: L(() => import('../features/plus/SiteLink.js'), 'SiteLink'),
})
const accountData = createRoute({
  getParentRoute: () => shell,
  path: '/account/data',
  component: L(() => import('../features/settings/AccountData.js'), 'AccountData'),
})
const accountRecovery = createRoute({
  getParentRoute: () => shell,
  path: '/account/recovery',
  // Ways back in is a card on Account now; old links land there.
  component: () => <Navigate to="/account" replace />,
})
const activityList = createRoute({
  getParentRoute: () => shell,
  path: '/you/activity',
  component: L(() => import('../features/chat/Inbox.js'), 'InboxActivity'),
})
// The Favorites page became the Favorites tab of Community (decision 201).
const favoritesList = createRoute({
  getParentRoute: () => shell,
  path: '/you/favorites',
  beforeLoad: () => {
    throw redirect({ ...OLD_FAVORITES, replace: true })
  },
})
// Community (decision 201): the tab is in the address (groups/tabs.ts).
const gatherings = createRoute({
  getParentRoute: () => shell,
  path: '/you/gatherings',
  component: L(() => import('../features/groups/Yours.js'), 'Yours'),
  validateSearch: yoursSearch,
})
// Partners (§5.18, decision 211): Partner tools, a partner's page, and Shared with partners.
const partnersMine = createRoute({
  getParentRoute: () => shell,
  path: '/you/partner',
  component: L(() => import('../features/partners/PartnersMine.js'), 'PartnersMine'),
})
const partnerTools = createRoute({
  getParentRoute: () => shell,
  path: '/you/partner/$id',
  component: L(() => import('../features/partners/PartnerTools.js'), 'PartnerTools'),
  validateSearch: partnerSearch,
})
const partnerDeliveries = createRoute({
  getParentRoute: () => shell,
  path: '/you/partner/$id/endpoints/$eid',
  component: L(() => import('../features/partners/Deliveries.js'), 'Deliveries'),
})
const partnerPage = createRoute({
  getParentRoute: () => shell,
  path: '/partner/$id',
  component: L(() => import('../features/partners/PartnerPage.js'), 'PartnerPage'),
})
const shared = createRoute({
  getParentRoute: () => shell,
  path: '/you/shared',
  component: L(() => import('../features/partners/Shared.js'), 'Shared'),
})
const reportBug = createRoute({
  getParentRoute: () => shell,
  path: '/bug',
  component: L(() => import('../features/bugs/ReportBug.js'), 'ReportBug'),
  validateSearch: (s: Record<string, unknown>): { from?: string } => ({ from: str(s.from) }),
})
const notesList = createRoute({
  getParentRoute: () => shell,
  path: '/you/notes',
  component: L(() => import('../features/people/Lists.js'), 'Notes'),
})
const whoLooked = createRoute({
  getParentRoute: () => shell,
  path: '/you/viewers',
  component: L(() => import('../features/people/Lists.js'), 'WhoLooked'),
})
const account = createRoute({
  getParentRoute: () => shell,
  path: '/account',
  component: L(() => import('../features/settings/Account.js'), 'Account'),
})
const youProfile = createRoute({
  getParentRoute: () => shell,
  path: '/you/profile',
  component: L(() => import('../features/profile/Profile.js'), 'Profile'),
  /**
   * `tab`: Health, Photos or Socials (else Profile, the editor); `sub`: Photos' Albums half (else Public
   * photos); `section`: open the editor at About you, Statistics or Preferences; `first`: straight from
   * onboarding, so Skip for now. Albums was a tab of its own: `?tab=albums` still opens it, now in Photos.
   */
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    first?: boolean
    tab?: 'health' | 'photos' | 'socials'
    sub?: 'albums'
    section?: 'about' | 'statistics' | 'preferences'
  } => ({
    first: s.first === true || s.first === 'true' ? true : undefined,
    tab:
      s.tab === 'albums'
        ? 'photos'
        : s.tab === 'photos' || s.tab === 'health' || s.tab === 'socials'
          ? s.tab
          : undefined,
    sub: s.tab === 'albums' || (s.tab === 'photos' && s.sub === 'albums') ? 'albums' : undefined,
    section:
      s.section === 'about' || s.section === 'statistics' || s.section === 'preferences'
        ? s.section
        : undefined,
  }),
})
// Photos, Albums, Health and the editor were screens of their own; now they're your profile's tabs
// (bugs.md), Albums inside Photos, and their old addresses (notifications, Learn, the Gate's checklist) open them.
const youPhotos = createRoute({
  getParentRoute: () => shell,
  path: '/you/photos',
  beforeLoad: () => {
    throw redirect({ to: '/you/profile', search: { tab: 'photos' }, replace: true })
  },
})
const youAlbums = createRoute({
  getParentRoute: () => shell,
  path: '/you/albums',
  beforeLoad: () => {
    throw redirect({ to: '/you/profile', search: { tab: 'photos', sub: 'albums' }, replace: true })
  },
})
const youAlbum = createRoute({
  getParentRoute: () => shell,
  path: '/you/albums/$id',
  component: L(() => import('../features/profile/Albums.js'), 'AlbumManage'),
})
const youStats = createRoute({
  getParentRoute: () => shell,
  path: '/you/stats',
  validateSearch: (s: Record<string, unknown>): { section?: 'about' | 'statistics' | 'preferences' } => ({
    section:
      s.section === 'about' || s.section === 'statistics' || s.section === 'preferences'
        ? s.section
        : undefined,
  }),
  beforeLoad: ({ search }) => {
    throw redirect({ to: '/you/profile', search: { section: search.section }, replace: true })
  },
})
const youHealth = createRoute({
  getParentRoute: () => shell,
  path: '/you/health',
  beforeLoad: () => {
    throw redirect({ to: '/you/profile', search: { tab: 'health' }, replace: true })
  },
})
const youRules = createRoute({
  getParentRoute: () => shell,
  path: '/you/rules',
  component: L(() => import('../features/entry/rules.js'), 'RulesView'),
})
const youVisibility = createRoute({
  getParentRoute: () => shell,
  path: '/you/visibility',
  component: L(() => import('../features/profile/Visibility.js'), 'Visibility'),
})
const invites = createRoute({
  getParentRoute: () => shell,
  path: '/invites',
  component: L(() => import('../features/trust/Invites.js'), 'Invites'),
  validateSearch: (s: Record<string, unknown>): { tab?: 'open' | 'people' } => ({
    tab: s.tab === 'open' || s.tab === 'people' ? s.tab : undefined,
  }),
})
const vouch = createRoute({
  getParentRoute: () => shell,
  path: '/invites/new',
  component: L(() => import('../features/trust/Vouch.js'), 'Vouch'),
  /** `for`: an Anonymous user to vouch for from a chat (FR-ANO-06); `back`: where Cancel returns */
  validateSearch: (s: Record<string, unknown>): { for?: string; handle?: string; back?: string } => ({
    for: str(s.for),
    handle: str(s.handle),
    back: str(s.back),
  }),
})
const inviteMade = createRoute({
  getParentRoute: () => shell,
  path: '/invites/$id',
  component: L(() => import('../features/trust/Invites.js'), 'InviteCreated'),
  /** `emailed`: whether the address given on Before you vouch was sent to (never the address itself) */
  validateSearch: (s: Record<string, unknown>): { emailed?: 'sent' | 'failed' } => ({
    emailed: s.emailed === 'sent' || s.emailed === 'failed' ? s.emailed : undefined,
  }),
})
// Shared codes (founders and the owner): one code for many, for mailings and cards at bars.
const sharedNew = createRoute({
  getParentRoute: () => shell,
  path: '/invites/shared/new',
  component: L(() => import('../features/trust/Shared.js'), 'SharedNew'),
})
const sharedCode = createRoute({
  getParentRoute: () => shell,
  path: '/invites/shared/$id',
  component: L(() => import('../features/trust/Shared.js'), 'SharedCode'),
})
const chain = createRoute({
  getParentRoute: () => shell,
  path: '/chain',
  component: L(() => import('../features/trust/Gate.js'), 'GateChain'),
})
const report = createRoute({
  getParentRoute: () => shell,
  path: '/report',
  component: L(() => import('../features/safety/Report.js'), 'Report'),
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    kind?: string
    id?: string
    userId?: string
    handle?: string
    tier?: string
    conversation?: string
    blocked?: string
    back?: string
    /** a spot_contact report: which of the spot's details */
    field?: 'phone' | 'website'
  } => ({
    kind: str(s.kind),
    id: str(s.id),
    userId: str(s.userId),
    handle: str(s.handle),
    tier: str(s.tier),
    conversation: str(s.conversation),
    blocked: str(s.blocked),
    back: str(s.back),
    field: s.field === 'phone' || s.field === 'website' ? s.field : undefined,
  }),
})
const announcements = createRoute({
  getParentRoute: () => shell,
  path: '/announcements',
  component: L(() => import('../features/news/Announcements.js'), 'Announcements'),
})
const safety = createRoute({
  getParentRoute: () => shell,
  path: '/safety',
  component: L(() => import('../features/safety/SafetyCenter.js'), 'SafetyCenter'),
})
// Learn More (content/learn): public, so an article can be shared with anyone. In the shell, so a
// member reads it in a popup over the map; the shell shows it as the page itself to anyone signed out.
const learn = createRoute({
  getParentRoute: () => shell,
  path: '/learn',
  component: L(() => import('../features/learn/Learn.js'), 'LearnIndex'),
})
const learnArticle = createRoute({
  getParentRoute: () => shell,
  path: '/learn/$slug',
  // An article that took its section's prefix (LEARN_MOVED): its old address goes to the new one.
  beforeLoad: ({ params, location }) => {
    const slug = LEARN_MOVED[params.slug]
    if (slug)
      throw redirect({
        to: '/learn/$slug',
        params: { slug },
        hash: location.hash || undefined,
        replace: true,
      })
  },
  component: L(() => import('../features/learn/Learn.js'), 'LearnArticleView'),
})
// Where the articles used to be: About and the Safety Center's own pages.
const about = createRoute({
  getParentRoute: () => root,
  path: '/about',
  component: () => <Navigate to="/learn" replace />,
})
const aboutArticle = createRoute({
  getParentRoute: () => root,
  path: '/about/$slug',
  component: function AboutArticle() {
    const { slug } = aboutArticle.useParams()
    return <Navigate to="/learn/$slug" params={{ slug }} replace />
  },
})
const safetyArticle = createRoute({
  getParentRoute: () => root,
  path: '/safety/$slug',
  component: function SafetyArticle() {
    const { slug } = safetyArticle.useParams()
    return <Navigate to="/learn/$slug" params={{ slug }} replace />
  },
})
const standing = createRoute({
  getParentRoute: () => shell,
  path: '/you/standing',
  // ?id= opens one item, as a warning's notification does.
  validateSearch: (s: Record<string, unknown>): { id?: string } => ({ id: str(s.id) }),
  component: L(() => import('../features/safety/Standing.js'), 'Standing'),
})
const myReports = createRoute({
  getParentRoute: () => shell,
  path: '/you/reports',
  component: L(() => import('../features/safety/Reports.js'), 'Reports'),
})
const blocked = createRoute({
  getParentRoute: () => shell,
  path: '/you/blocked',
  component: L(() => import('../features/safety/Reports.js'), 'Blocked'),
})
const spotNew = createRoute({
  getParentRoute: () => shell,
  path: '/spots/new',
  component: L(() => import('../features/spots/SpotForm.js'), 'SpotNew'),
  validateSearch: (s: Record<string, unknown>): { lat?: number; lng?: number } => ({
    lat:
      typeof s.lat === 'number'
        ? s.lat
        : Number.isFinite(Number(s.lat)) && s.lat !== undefined
          ? Number(s.lat)
          : undefined,
    lng:
      typeof s.lng === 'number'
        ? s.lng
        : Number.isFinite(Number(s.lng)) && s.lng !== undefined
          ? Number(s.lng)
          : undefined,
  }),
})
const spotDetail = createRoute({
  getParentRoute: () => shell,
  path: '/spots/$id',
  component: L(() => import('../features/spots/SpotDetail.js'), 'SpotDetail'),
})
const spotEdit = createRoute({
  getParentRoute: () => shell,
  path: '/spots/$id/edit',
  component: L(() => import('../features/spots/SpotForm.js'), 'SpotEdit'),
})
// Events (decision 91): new from a long-press or from Hosting, the event's page, and the host's list.
const eventNew = createRoute({
  getParentRoute: () => shell,
  path: '/events/new',
  component: L(() => import('../features/events/EventForm.js'), 'EventNew'),
  validateSearch: (
    s: Record<string, unknown>,
  ): { lat?: number; lng?: number; from?: 'hosting'; group?: string } => ({
    lat: Number.isFinite(Number(s.lat)) && s.lat !== undefined ? Number(s.lat) : undefined,
    lng: Number.isFinite(Number(s.lng)) && s.lng !== undefined ? Number(s.lng) : undefined,
    from: s.from === 'hosting' ? 'hosting' : undefined,
    group: str(s.group),
  }),
})
const eventDetail = createRoute({
  getParentRoute: () => shell,
  path: '/events/$id',
  component: L(() => import('../features/events/EventDetail.js'), 'EventDetail'),
})
const eventEdit = createRoute({
  getParentRoute: () => shell,
  path: '/events/$id/edit',
  component: L(() => import('../features/events/EventForm.js'), 'EventEdit'),
  // A repeating event's (decision 158): this one, it and the ones after, or all still to come.
  validateSearch: (s: Record<string, unknown>): { scope?: 'following' | 'all' } => ({
    scope: s.scope === 'following' || s.scope === 'all' ? s.scope : undefined,
  }),
})
const eventGuests = createRoute({
  getParentRoute: () => shell,
  path: '/events/$id/guests',
  component: L(() => import('../features/events/EventGuests.js'), 'EventGuests'),
})
// An event's Meet (decision 193): the host's map of who's coming, and the list.
const eventMeet = createRoute({
  getParentRoute: () => shell,
  path: '/events/$id/meet',
  component: L(() => import('../features/events/EventMeetScreen.js'), 'EventMeetScreen'),
})
// Groups (decision 92): new from a long-press, the group's page, its members, and an admin's edit.
const groupNew = createRoute({
  getParentRoute: () => shell,
  path: '/groups/new',
  component: L(() => import('../features/groups/GroupForm.js'), 'GroupNew'),
  validateSearch: (s: Record<string, unknown>): { lat?: number; lng?: number } => ({
    lat: Number.isFinite(Number(s.lat)) && s.lat !== undefined ? Number(s.lat) : undefined,
    lng: Number.isFinite(Number(s.lng)) && s.lng !== undefined ? Number(s.lng) : undefined,
  }),
})
const groupDetail = createRoute({
  getParentRoute: () => shell,
  path: '/groups/$id',
  component: L(() => import('../features/groups/GroupDetail.js'), 'GroupDetail'),
})
const groupEdit = createRoute({
  getParentRoute: () => shell,
  path: '/groups/$id/edit',
  component: L(() => import('../features/groups/GroupForm.js'), 'GroupEdit'),
})
const groupMembersRoute = createRoute({
  getParentRoute: () => shell,
  path: '/groups/$id/members',
  component: L(() => import('../features/groups/GroupMembers.js'), 'GroupMembers'),
  validateSearch: (s: Record<string, unknown>): { tab?: 'asking' } => ({
    tab: s.tab === 'asking' ? 'asking' : undefined,
  }),
})
// A meet (§5.17, decision 163): its map and next step, opened from the thread's meet bar or card.
const meet = createRoute({
  getParentRoute: () => shell,
  path: '/meet/$id',
  component: L(() => import('../features/meet/MeetScreen.js'), 'MeetScreen'),
  // `friend`: just set off with Tell a friend on, so its sheet opens first (FR-MET-18)
  validateSearch: (s: Record<string, unknown>): { friend?: boolean } =>
    s.friend === true || s.friend === 'true' ? { friend: true } : {},
})
// Tell a friend's page (FR-MET-18): no account, outside the app; the key is in the link's fragment.
const friend = createRoute({
  getParentRoute: () => root,
  path: '/friend/$token',
  component: L(() => import('../features/meet/FriendPage.js'), 'FriendPage'),
})
const user = createRoute({
  getParentRoute: () => shell,
  path: '/u/$id',
  component: L(() => import('../features/profile/FullProfile.js'), 'FullProfile'),
})

export const routeTree = root.addChildren([
  frontDoorRoute,
  landing,
  about,
  aboutArticle,
  safetyArticle,
  age,
  anon,
  invite,
  friend,
  join,
  rules,
  terms,
  privacy,
  install,
  location,
  notify,
  signIn,
  signInPasskey,
  signInVerify,
  continueFromSafari,
  leave,
  shell.addChildren([
    announcements,
    learn,
    learnArticle,
    map,
    grid,
    wall,
    wallSaved,
    gate,
    gateRecord,
    gateReview,
    inbox.addChildren([thread]),
    you,
    settings,
    notifications,
    quickReplies,
    activityList,
    accountData,
    accountRecovery,
    plus,
    plusSite,
    account,
    youProfile,
    favoritesList,
    gatherings,
    partnersMine,
    partnerTools,
    partnerDeliveries,
    partnerPage,
    shared,
    notesList,
    reportBug,
    whoLooked,
    youPhotos,
    youAlbums,
    youAlbum,
    youStats,
    youHealth,
    youVisibility,
    youRules,
    invites,
    vouch,
    inviteMade,
    sharedNew,
    sharedCode,
    chain,
    report,
    safety,
    myReports,
    standing,
    blocked,
    spotNew,
    spotDetail,
    spotEdit,
    eventNew,
    eventDetail,
    eventEdit,
    eventGuests,
    eventMeet,
    groupNew,
    groupDetail,
    groupEdit,
    groupMembersRoute,
    user,
    meet,
  ]),
])
// A new page starts at the top; Back returns to where you were (iOS testing: the Continue screen
// opened half scrolled under the header).
// A #anchor is Learn More's to scroll to (its articles scroll inside their page, under pinned
// headings: features/learn); the router's own jump would scroll the wrong box and overshoot.
export const router = createRouter({
  routeTree,
  scrollRestoration: true,
  defaultHashScrollIntoView: false,
  // A screen that crashes offers the update that usually fixes it, rather than the router's bare message.
  defaultErrorComponent: ErrorScreen,
})
// Every screen's Back reads where it was opened from (app/trail.ts).
router.subscribe('onResolved', ({ toLocation }) =>
  recordTrail(toLocation.state.__TSR_index ?? 0, toLocation.pathname),
)

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
